import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import "./AddCandidate.css";
import { FaUser } from "react-icons/fa6";
import { IoIosAddCircleOutline } from "react-icons/io";
import config from "../../../config";
import React, { useEffect, useMemo, useState } from "react";
import { VscChevronRight } from "react-icons/vsc";
import SuccessPopup from "../../../components/common/SuccessPopup";
import ErrorPopup from "../../../components/common/ErrorPopup";
import ConfirmPopup from "../../../components/common/ConfirmPopup";
import { LuPencilLine } from "react-icons/lu";
import { GrNotification } from "react-icons/gr";
import { IoIosArrowBack } from "react-icons/io";
import Header from "../../../components/common/Header";

const AddCandidate = ({
  onCancel,
  selectedCandidate,
  onDelete,
  onConvertToOnboarding,
  onSave,
}) => {
  const isEditMode = !!selectedCandidate && selectedCandidate.id != null;
  const [isViewMode, setIsViewMode] = useState(isEditMode);
  const [successPopup, setSuccessPopup] = useState(null);
  const [errorPopup, setErrorPopup] = useState(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [clients, setClients] = useState([]);
  const [recruitments, setRecruitments] = useState([]);
  const [clientTypes, setClientTypes] = useState([]);
  const [subVendors, setSubVendors] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [qualifications, setQualifications] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");

  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingRecs, setLoadingRecs] = useState(true);
  const [clientError, setClientError] = useState(null);
  const [recError, setRecError] = useState(null);

  const [userDetails, setUserDetails] = useState({
    name: "",
    role: "",
    token: "",
    orgId: "",
  });

  useEffect(() => {
    const token = localStorage.getItem("Authorization") || "";
    const firstName = localStorage.getItem("firstName") || "";
    const lastName = localStorage.getItem("lastName") || "";
    const role = localStorage.getItem("role")?.toUpperCase() || "GUEST";
    const orgId = localStorage.getItem("orgId") || "";
    setUserDetails({
      name: `${firstName} ${lastName}`.trim(),
      role,
      token,
      orgId,
    });
  }, []);

  // Fetch SubVendors
  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;

    const fetchSubVendors = async () => {
      try {
        const url = `${config.API_BASE_URL}${config.SUBVENDOR_LIST_ENDPOINT}?orgKey=${userDetails.orgId}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        const data = await response.json();
        console.log("SubVendor API Response:", data); // ✅ debug

        const vendors = Array.isArray(data?.body) ? data.body : [];
        setSubVendors(vendors);
      } catch (err) {
        console.error("SubVendor API Error:", err);
        setSubVendors([]);
      }
    };

    fetchSubVendors();
  }, [userDetails.token, userDetails.orgId]);


  // --- Validation ---
  const validationSchema = Yup.object({
    candidateName: Yup.string()
      .max(100, "Candidate Name must be at most 100 characters")
      .required("Candidate Name is required"),

    aadharNumber: Yup.string()
      .required("Aadhar Number is required.")
      .matches(/^[0-9]{12}$/, "Aadhar must be exactly 12 digits"),

    uanNumber: Yup.string()
      .required("UAN Number is required.")
      .matches(/^[0-9]{12}$/, "UAN must be exactly 12 digits"),

    phoneNumber: Yup.string()
      .matches(/^\+91\s?\d{10}$/, "Use +91 XXXXXXXXXX format.")
      .required("Phone is required."),

    emailId: Yup.string()
      .email("Enter a valid email.")
      .test(
        "no-uppercase",
        "Email should not contain uppercase letters.",
        (value) => !value || value === value.toLowerCase()
      )
      .required("Email address is required."),

    dob: Yup.string()
      .required("Date of Birth is required.")
      .test(
        "dob-valid",
        "DOB cannot be in the future and candidate must be at least 18 years old",
        (val) => {
          if (!val) return false;
          const dob = new Date(val);
          const today = new Date();
          // Normalize times
          dob.setHours(0, 0, 0, 0);
          today.setHours(0, 0, 0, 0);
          if (dob > today) return false; // not in future
          // compute age
          let age = today.getFullYear() - dob.getFullYear();
          const m = today.getMonth() - dob.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            age--;
          }
          return age >= 18;
        }
      ),

    gender: Yup.string()
      .oneOf(["Male", "Female", "Other"])
      .required("Gender is required."),

    address: Yup.string()
      .required("Address Line is required.")
      .max(100, "Address cannot exceed 100 characters"),

    city: Yup.string()
      .max(50, "City must be at most 50 characters")
      .required("City is required."),

    district: Yup.string().required("District is required."),

    state: Yup.string().required("State is required."),

    pincode: Yup.string()
      .matches(/^[0-9]{6}$/, "Pincode must be exactly 6 digits")
      .required("Pincode is required."),

    country: Yup.string().required("Country is required."),

    client: Yup.string().required("Client Name is required."),

    interviewDate: Yup.string()
      .required("Interview date is required.")
      .test(
        "not-past-date",
        "Interview date cannot be in the past",
        (value) => {
          if (!value) return false;

          const selectedDate = new Date(value);
          const today = new Date();

          // remove time part
          selectedDate.setHours(0, 0, 0, 0);
          today.setHours(0, 0, 0, 0);

          return selectedDate >= today;
        }
      ),

    interviewTime: Yup.string()
      .matches(/^([01]\d|2[0-3]):[0-5]\d$/, "Time must be HH:mm (24-hour)")
      .required("Interview time is required."),

    jobRole: Yup.string().required("Job Role is required."),

    subVendor: Yup.string().required("Sub Vendor Name is required."),

    qualification: Yup.string().required("Qualification is required."),

    skills: Yup.string()
      .required("Skills are required.")
      .max(50, "Skills must be at most 50 characters"),

    pointOfContactInterview: Yup.string().required(
      "Point of Contact is required."
    ),

    pocRole: Yup.string()
      .required("POC Role is required.")
      .max(21, "POC Role must be at most 21 characters")
      .matches(/^[A-Za-z0-9 ]*$/, "Only letters, numbers and spaces allowed"),
  });

  const blankInitialValues = {
    candidateName: "",
    aadharNumber: "",
    uanNumber: "",
    emailId: "",
    phoneNumber: "+91 ",
    dob: "",
    gender: "",
    address: "",
    city: "",
    district: "",
    state: "",
    country: "",
    pincode: "",
    client: "",
    recruitmentName: "",
    jobRole: "",
    skills: "",
    subVendorName: "",
    interviewDate: "",
    interviewTime: "",
    qualification: "",
    pointOfContactInterview: "",
    pocRole: "",
  };

  // 🔹 Map selectedCandidate → form values
  const initialValues = useMemo(() => {
    if (!isEditMode || !selectedCandidate) return blankInitialValues;

    const c = selectedCandidate;
    const normalizedPhone =
      c.phone && c.phone.startsWith("+91")
        ? `+91 ${c.phone.replace("+91", "").trim()}`
        : c.phone || "+91 ";

    return {
      candidateName: c.name || "",
      aadharNumber: c.aadhar || "",
      uanNumber: c.uan || "",
      emailId: c.email || "",
      phoneNumber: normalizedPhone,
      dob: c.dob || "",
      gender: c.gender || "",
      address: c.addressLine1 || "",
      city: c.city || "",
      district: c.district || "",
      state: c.state || "",
      country: c.country || "",
      pincode: c.pinCode || "",
      client: c.clientName || "",
      recruitmentName: c.RecruitmentKey ? String(c.RecruitmentKey) : (c.recruitmentId ? String(c.recruitmentId) : ""),
      jobRole: c.jobRole || "",
      skills: c.skillsConfigured || "",
      subVendor: c.subVendorName || "",
      interviewDate: c.interviewDate || "",
      interviewTime: c.interviewTime || "",
      qualification: c.qualification || "",
      pointOfContactInterview: c.pocName || "",
      pocRole: c.pocRole || "",
    };
  }, [isEditMode, selectedCandidate]);

  // helpful for cascading dropdowns when editing
  useEffect(() => {
    if (selectedCandidate) {
      if (selectedCandidate.country)
        setSelectedCountry(selectedCandidate.country);
      if (selectedCandidate.state) setSelectedState(selectedCandidate.state);
    }
  }, [selectedCandidate]);

  // --- Submit (create + update) ---
  const handleSubmit = async (values, { setSubmitting }) => {
    console.log("FORM SUBMITTED 🔥", values);
    try {
      const normalizedPhone = values.phoneNumber.replace(/\s+/g, "");

      const requestBody = {
        name: values.candidateName,
        aadhar: values.aadharNumber,
        uan: values.uanNumber,
        email: values.emailId,
        phone: normalizedPhone,
        dob: values.dob,
        gender: values.gender,
        addressLine1: values.address,
        city: values.city,
        district: values.district,
        state: values.state,
        country: values.country,
        pinCode: values.pincode,
        clientName: values.client,
        recruitmentId: values.recruitmentName
          ? Number(values.recruitmentName)
          : null,
        jobRole: values.jobRole,
        skillsConfigured: values.skills,

        subVendorName: values.subVendor,   // ✅ add this line

        interviewDate: values.interviewDate,
        interviewTime: values.interviewTime,
        qualification: values.qualification,
        pocName: values.pointOfContactInterview,
        pocRole: values.pocRole,
        status: selectedCandidate?.status || "INITIAL",
        candidateKey: selectedCandidate?.candidateKey,
        orgKey: Number(userDetails.orgId),
      };


      const isEdit = isEditMode;
      const id =
        isEdit && selectedCandidate && selectedCandidate.id != null
          ? selectedCandidate.id
          : null;

      const baseUrl = `${config.API_BASE_URL}${config.ADD_CANDIDATES_ENDPOINT}`;
      const url = isEdit && id != null ? `${baseUrl}/${id}` : `${baseUrl}`;

      const method = isEdit && id != null ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
          orgKey: userDetails.orgId,
        },

        body: JSON.stringify(requestBody),
        credentials: "include",
      });

      const contentType = response.headers.get("content-type") || "";
      let body;
      if (contentType.includes("application/json")) {
        body = await response.json();
      } else {
        body = await response.text();
      }

      if (response.ok) {
        const ref =
          typeof body === "object" && body
            ? body.candidateKey || body.key || body.id
            : null;
        setSuccessPopup(
          isEdit
            ? "Candidate updated successfully!"
            : ref
              ? `Candidate created successfully (Ref: ${ref}).`
              : "Candidate created successfully!"
        );
      } else {
        const msg =
          typeof body === "object" && body && (body.message || body.error)
            ? body.message || body.error
            : `${body}`;
        setErrorPopup(`Error: ${msg}`);
      }
    } catch (err) {
      console.error(err);
      setErrorPopup("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // --- Common dropdown fetcher (Config APIs) ---
  const fetchDropdownOptions = async (type, setter) => {
    try {
      const res = await fetch(
        `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${userDetails.orgId}?configType=${type}`,
        {
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      if (!res.ok) throw new Error("Failed to fetch " + type);
      const data = await res.json();
      const active = (Array.isArray(data) ? data : []).filter(
        (i) => i.isActive
      );

      if (setter === setJobRoles || setter === setQualifications) {
        const vals = active
          .map((i) => (typeof i.values === "string" ? i.values.trim() : ""))
          .filter(Boolean);
        setter([...new Set(vals)]);
      } else {
        setter(active);
      }
    } catch (e) {
      console.error(`Error fetching ${type}:`, e);
    }
  };

  const loadJobRoles = () => fetchDropdownOptions("JobRole", setJobRoles);
  const loadQualifications = () =>
    fetchDropdownOptions("Qualification", setQualifications);

  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;
    fetchDropdownOptions("ClientType", setClientTypes);
    loadJobRoles();
    loadQualifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails.token, userDetails.orgId]);

  // Countries -> States -> Districts
  useEffect(() => {
    const run = async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}${config.COUNTRY_ENDPOINT}`,
          {
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          }
        );
        if (!res.ok) throw new Error("Failed to fetch countries");
        const data = await res.json();
        const countriesList = Array.isArray(data) ? data : (data?.body || data?.data || []);
        setCountries(countriesList);
      } catch (e) {
        console.error(e);
      }
    };
    run();
  }, []);

  useEffect(() => {
    if (!selectedCountry) return;
    const run = async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}${config.STATE_ENDPOINT}?countryId=${selectedCountry}`,
          {
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          }
        );
        if (!res.ok) throw new Error("Failed to fetch states");
        const data = await res.json();
        const statesList = Array.isArray(data) ? data : (data?.body || data?.data || []);
        setStates(statesList);
      } catch (e) {
        console.error(e);
      }
    };
    run();
  }, [selectedCountry]);

  useEffect(() => {
    if (!selectedState) return;
    const run = async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}${config.DISTRICT_ENDPOINT}?stateId=${selectedState}`,
          {
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          }
        );
        if (!res.ok) throw new Error("Failed to fetch districts");
        const data = await res.json();
        const districtsList = Array.isArray(data) ? data : (data?.body || data?.data || []);
        setDistricts(districtsList);
      } catch (e) {
        console.error(e);
      }
    };
    run();
  }, [selectedState]);

  // ---- Fetch CLIENTS ----
  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;

    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        const response = await fetch(
          `${config.API_BASE_URL}${config.CLIENT_LIST_ENDPOINT}${userDetails.orgId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${userDetails.token}`,
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch clients (HTTP ${response.status})`);
        }

        const data = await response.json();
        const normalized = Array.isArray(data.body)
          ? data.body
            .filter((c) => c && typeof c.name === "string")
            .map((c) => ({
              name: c.name,
              clientKey: c.clientKey,
              primaryContactName: c.primaryContactname || c.primaryContactName || "",
              primaryContactRole: c.role || c.primaryContactRole || ""
            }))
          : [];

        setClients(normalized);
        setClientError(null);
      } catch (err) {
        console.error("Client fetch error:", err);
        setClientError("Could not load clients.");
        setClients([]);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, [userDetails.token, userDetails.orgId]);

  // ---- Fetch RECRUITMENTS ----
  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;

    const fetchRecruitments = async () => {
      setLoadingRecs(true);
      try {
        const url = `${config.API_BASE_URL}${config.RECRUITMENT_LIST_ENDPOINT}?orgKey=${userDetails.orgId}`;
        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        if (!res.ok)
          throw new Error(`Failed to fetch recruitments (HTTP ${res.status})`);
        const data = await res.json();

        const list = Array.isArray(data?.content) ? data.content : [];
        const normalized = list
          .filter((r) => r && typeof r.recruitmentName === "string")
          .map((r) => ({
            id: r.id,
            recruitmentKey: r.recruitmentKey,
            recruitmentName: r.recruitmentName,
            clientName: r.clientName,
            status: r.status,
          }));

        setRecruitments(normalized);
        setRecError(null);
      } catch (e) {
        console.error("Recruitment fetch error:", e);
        setRecError("Could not load recruitments.");
        setRecruitments([]);
      } finally {
        setLoadingRecs(false);
      }
    };

    fetchRecruitments();
  }, [userDetails.token, userDetails.orgId]);

  // --- DELETE candidate ---
  const handleDelete = () => {
    if (!isEditMode || !selectedCandidate) {
      setErrorPopup("No candidate selected to delete.");
      return;
    }

    const id =
      selectedCandidate && selectedCandidate.id != null
        ? String(selectedCandidate.id)
        : null;

    if (!id) {
      setErrorPopup("No valid candidate id found.");
      return;
    }

    setShowConfirmPopup(true);
  };

  const handleDeleteConfirm = async () => {
    setShowConfirmPopup(false);

    if (!isEditMode || !selectedCandidate) {
      setErrorPopup("No candidate selected to delete.");
      return;
    }

    const id =
      selectedCandidate && selectedCandidate.id != null
        ? String(selectedCandidate.id)
        : null;

    if (!id) {
      setErrorPopup("No valid candidate id found.");
      return;
    }

    const url = `${config.API_BASE_URL}${config.ADD_CANDIDATES_ENDPOINT}/${id}?orgKey=${userDetails.orgId}`;
    console.log("Deleting candidate at URL:", url);

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      const text = await response.text();
      console.log("Delete response text:", text);

      if (!response.ok) {
        setErrorPopup(`Unexpected response: ${text}`);
        return;
      }

      setSuccessPopup("Candidate deleted successfully!");
      setIsDeleted(true);
    } catch (err) {
      console.error("Delete candidate error:", err);
      setErrorPopup("Failed to delete candidate. Please try again.");
    }
  };

  const isReadOnly = isViewMode;
  console.log("selectedCandidate:", selectedCandidate);
  return (
    <div>
      {successPopup && (
        <SuccessPopup
          message={successPopup}
          onClose={() => {
            setSuccessPopup(null);
            if (isDeleted) {
              if (onDelete) onDelete(selectedCandidate);
            } else {
              if (onSave) onSave();
              onCancel && onCancel();
            }
          }}
        />
      )}
      {errorPopup && (
        <ErrorPopup message={errorPopup} onClose={() => setErrorPopup(null)} />
      )}
      {showConfirmPopup && (
        <ConfirmPopup
          message="Are you sure you want to delete this candidate?"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setShowConfirmPopup(false)}
        />
      )}

      {/* Header */}
      {/* Header */}
      <Header
        showBack={true}
        onBack={onCancel}
        title={
          isViewMode
            ? isEditMode
              ? "View Candidate"
              : "Add Candidate"
            : isEditMode
              ? "Edit Candidate"
              : "Add Candidate"
        }
        subtitle={
          <>
            All Candidate <VscChevronRight style={{ verticalAlign: "middle" }} />{" "}
            {isViewMode
              ? "View Candidate"
              : isEditMode
                ? "Edit Candidate"
                : "Add Candidate"}
          </>
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form className='add-client-form'>
            {/* top action bar (only when editing existing) */}
            {isEditMode && (
              <div className='client-form-action-bar'>
                {selectedCandidate.status !== "ACCEPTED" ? (
                  <button
                    type='button'
                    className='client-delete-btn'
                    onClick={handleDelete}
                  >
                    <span className='client-delete-icon'>🗑</span>
                    <span>Delete</span>
                  </button>
                ) : (
                  ""
                )}

                <button
                  type='button'
                  className='client-edit-btn'
                  onClick={() => {
                    if (selectedCandidate.status === "ACCEPTED") {
                      onConvertToOnboarding(selectedCandidate);
                    } else {
                      setIsViewMode(!isViewMode);
                    }
                  }}
                >
                  <span className='client-edit-icon'>
                    <LuPencilLine />
                  </span>

                  <span>
                    {selectedCandidate.status === "ACCEPTED"
                      ? "Convert to Onboarding"
                      : isViewMode
                        ? "Edit"
                        : "View"}
                  </span>
                </button>
              </div>
            )}

            {/* Candidate Information */}
            <section className='form-section'>
              <h3>
                <span className='icon'>
                  <FaUser />
                </span>{" "}
                Candidate Information
              </h3>
              <div className='form-grid'>
                <div className='input-group'>
                  <label>
                    Candidate Name <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='text'
                    name='candidateName'
                    disabled={isReadOnly}
                  />
                  <ErrorMessage
                    name='candidateName'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    Aadhar Number <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='text'
                    name='aadharNumber'
                    disabled={isReadOnly}
                  />
                  <ErrorMessage
                    name='aadharNumber'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    UAN Number <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='uanNumber' disabled={isReadOnly} />
                  <ErrorMessage
                    name='uanNumber'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    Email Address <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='email'
                    name='emailId'
                    disabled={isReadOnly}
                    onChange={(e) => {
                      if (isReadOnly) return;
                      setFieldValue("emailId", e.target.value.toLowerCase());
                    }}
                  />                  <ErrorMessage
                    name='emailId'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    Phone Number <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='text'
                    name='phoneNumber'
                    disabled={isReadOnly}
                    onChange={(e) => {
                      if (isReadOnly) return;
                      let value = e.target.value;
                      if (!value.startsWith("+91")) {
                        value = value.replace(/^\+?91\s?/, "");
                        value = "+91 " + value.replace(/\D/g, "").slice(0, 10);
                      } else {
                        value =
                          "+91 " +
                          value
                            .replace("+91", "")
                            .trim()
                            .replace(/\D/g, "")
                            .slice(0, 10);
                      }
                      setFieldValue("phoneNumber", value);
                    }}
                  />
                  <ErrorMessage
                    name='phoneNumber'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-row'>
                  <div className='input-group half-width'>
                    <label>
                      Date Of Birth
                      <span className='required-asterisk'>*</span>
                    </label>
                    <Field type='date' name='dob' disabled={isReadOnly} />
                    <ErrorMessage
                      name='dob'
                      className='error'
                      component='div'
                    />
                  </div>
                </div>

                <div className='input-group'>
                  <label>
                    Gender <span className='required-asterisk'>*</span>
                  </label>
                  <Field as='select' name='gender' disabled={isReadOnly}>
                    <option value=''>Select</option>
                    <option value='Male'>Male</option>
                    <option value='Female'>Female</option>
                    <option value='Other'>Other</option>
                  </Field>
                  <ErrorMessage
                    name='gender'
                    component='div'
                    className='error'
                  />
                </div>
              </div>
            </section>

            {/* Address Information */}
            <section className='form-section'>
              <h3>
                <span className='icon'>
                  <FaUser />
                </span>{" "}
                Address Information
              </h3>
              <div className='form-grid'>
                <div className='input-group'>
                  <label>
                    Country <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    as='select'
                    name='country'
                    disabled={isReadOnly}
                    onChange={(e) => {
                      if (isReadOnly) return;
                      const countryCode = e.target.value;
                      setFieldValue("country", countryCode);
                      setSelectedCountry(countryCode);
                      setFieldValue("state", "");
                      setFieldValue("district", "");
                      setStates([]);
                      setDistricts([]);
                    }}
                  >
                    <option value=''>Select</option>
                    {countries.map((c) => (
                      <option key={c.id || c.countryId} value={c.id || c.countryId}>
                        {c.countryName || c.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='country'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    State <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    as='select'
                    name='state'
                    disabled={isReadOnly}
                    onChange={(e) => {
                      if (isReadOnly) return;
                      const stateCode = e.target.value;
                      setFieldValue("state", stateCode);
                      setSelectedState(stateCode);
                      setFieldValue("district", "");
                      setDistricts([]);
                    }}
                  >
                    <option value=''>Select</option>
                    {states.map((s) => (
                      <option key={s.id || s.stateId} value={s.id || s.stateId}>
                        {s.countryName || s.stateName || s.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='state'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    District <span className='required-asterisk'>*</span>
                  </label>
                  <Field as='select' name='district' disabled={isReadOnly}>
                    <option value=''>Select</option>
                    {districts.map((d) => (
                      <option key={d.id || d.districtId} value={d.id || d.districtId}>
                        {d.countryName || d.districtName || d.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='district'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    City <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='city' disabled={isReadOnly} />
                  <ErrorMessage name='city' component='div' className='error' />
                </div>

                <div className='input-group'>
                  <label>
                    Address Line / Street
                    <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='address' disabled={isReadOnly} />
                  <ErrorMessage
                    name='address'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    Pincode <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='pincode' disabled={isReadOnly} />
                  <ErrorMessage
                    name='pincode'
                    component='div'
                    className='error'
                  />
                </div>
              </div>
            </section>

            {/* Job Information */}
            <section className='form-section'>
              <h3>
                <span className='icon'>
                  <FaUser />
                </span>
                <span>Job Information</span>
                <span className='add-icon'>
                  <IoIosAddCircleOutline />
                </span>
              </h3>
              <div className='form-grid'>
                <div className='input-group'>
                  <label>
                    Client <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    as='select'
                    name='client'
                    disabled={isReadOnly || loadingClients || !!clientError}
                    onChange={(e) => {
                      const selectedName = e.target.value;
                      setFieldValue("client", selectedName);

                      // Auto-fill POC details from selected client
                      const clientData = clients.find(c => c.name === selectedName);
                      if (clientData) {
                        setFieldValue("pointOfContactInterview", clientData.primaryContactName || "");
                        setFieldValue("pocRole", clientData.primaryContactRole || "");
                      }
                    }}
                  >
                    <option value=''>
                      {loadingClients
                        ? "Loading clients..."
                        : clientError
                          ? "Failed to load clients"
                          : "Select"}
                    </option>
                    {!loadingClients &&
                      !clientError &&
                      clients.map((c) => (
                        <option key={c.clientKey ?? c.name} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                  </Field>
                  <ErrorMessage
                    name='client'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>Recruitment Name</label>
                  <Field
                    as='select'
                    name='recruitmentName'
                    disabled={isReadOnly || loadingRecs || !!recError}
                  >
                    <option value=''>
                      {loadingRecs
                        ? "Loading recruitments..."
                        : recError
                          ? "Failed to load recruitments"
                          : "Select"}
                    </option>
                    {!loadingRecs &&
                      !recError &&
                      recruitments.map((r) => (
                        <option key={r.recruitmentKey ?? r.id} value={r.id}>
                          {r.recruitmentName}
                        </option>
                      ))}
                  </Field>
                  <ErrorMessage
                    name='recruitmentName'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    Job Role <span className='required-asterisk'>*</span>
                  </label>
                  <Field as='select' name='jobRole' disabled={isReadOnly}>
                    <option value=''>Select</option>
                    {jobRoles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='jobRole'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    Skills <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='skills' disabled={isReadOnly} />
                  <ErrorMessage
                    name='skills'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    Sub Vendor <span className='required-asterisk'></span>
                  </label>
                  {localStorage.getItem('userEmail') === 'nvguhan@wtilth.com' ? (
                    <Field type='text' name='subVendor' disabled={isReadOnly} placeholder="Enter Sub Vendor" />
                  ) : (
                    <Field as='select' name='subVendor' disabled={isReadOnly}>
                      <option value=''>Select</option>
                      {subVendors.map((vendor, index) => (
                        <option
                          key={index}
                          value={(vendor.subVendorName || vendor.name).trim()}
                        >
                          {(vendor.subVendorName || vendor.name).trim()}
                        </option>
                      ))}
                    </Field>
                  )}
                  <ErrorMessage
                    name='subVendor'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    Interview Date
                    <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='date'
                    name='interviewDate'
                    disabled={isReadOnly || isEditMode}
                    min={new Date().toISOString().split("T")[0]}
                  />
                  <ErrorMessage
                    name='interviewDate'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    Interview Time
                    <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='time'
                    name='interviewTime'
                    disabled={isReadOnly}
                  />
                  <ErrorMessage
                    name='interviewTime'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    Qualification <span className='required-asterisk'>*</span>
                  </label>
                  <Field as='select' name='qualification' disabled={isReadOnly}>
                    <option value=''>Select</option>
                    {qualifications.map((q) => (
                      <option key={q} value={q}>
                        {q}
                      </option>
                    ))}
                  </Field>
                </div>

                <div className='input-group'>
                  <label>
                    Point of Contact / Interviewer
                    <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='text'
                    name='pointOfContactInterview'
                    disabled={isReadOnly}
                  />
                  <ErrorMessage
                    name='pointOfContactInterview'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    POC Role <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='pocRole' disabled={isReadOnly} />
                  <ErrorMessage
                    name='pocRole'
                    component='div'
                    className='error'
                  />
                </div>
              </div>
            </section>

            {/* Actions */}
            <div className='button-container'>
              <button type='button' className='cancel-btn' onClick={onCancel}>
                {isViewMode ? "Back" : "Cancel"}
              </button>
              {!isViewMode && (
                <button
                  type='submit'
                  disabled={isSubmitting}
                  className='save-btn'
                >
                  {isSubmitting
                    ? isEditMode
                      ? "Updating..."
                      : "Saving..."
                    : isEditMode
                      ? "Update"
                      : "Add"}
                </button>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddCandidate;
